import { openDB } from 'idb';

// PDV offline (Fase 1) — tudo que o PDV precisa pra continuar vendendo com
// a internet caída: catálogo/config em cache (somente leitura, atualizados
// enquanto online) e a fila de vendas pendente de sincronização. Mesas/
// Comandas também ficam em cache, mas só pra EXIBIÇÃO offline — escrita
// (abrir mesa, lançar item) continua exigindo conexão (ver PDV.jsx).
const DB_NAME = 'pdv_offline';
const DB_VERSION = 1;

let dbPromise = null;
function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('produtos_cache')) {
          db.createObjectStore('produtos_cache', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('clientes_cache')) {
          db.createObjectStore('clientes_cache', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('config_cache')) {
          db.createObjectStore('config_cache', { keyPath: 'chave' });
        }
        if (!db.objectStoreNames.contains('mesas_cache')) {
          db.createObjectStore('mesas_cache', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('comandas_cache')) {
          // chave = mesa_id (é assim que a tela busca: GET /comandas/:mesaId)
          db.createObjectStore('comandas_cache', { keyPath: 'mesa_id' });
        }
        if (!db.objectStoreNames.contains('sync_queue')) {
          const store = db.createObjectStore('sync_queue', { keyPath: 'uuid' });
          store.createIndex('status', 'status');
        }
      },
    });
  }
  return dbPromise;
}

// ── Produtos ─────────────────────────────────────────────────
export async function cacheProdutos(produtos) {
  const db = await getDb();
  const tx = db.transaction('produtos_cache', 'readwrite');
  await tx.store.clear();
  await Promise.all(produtos.map(p => tx.store.put(p)));
  await tx.done;
}
export async function getProdutosCache() {
  const db = await getDb();
  return db.getAll('produtos_cache');
}

// ── Clientes (últimos usados/buscados — não é o cadastro inteiro) ──
export async function cacheCliente(cliente) {
  if (!cliente?.id) return;
  const db = await getDb();
  await db.put('clientes_cache', cliente);
}
export async function cacheClientes(clientes) {
  const db = await getDb();
  const tx = db.transaction('clientes_cache', 'readwrite');
  await Promise.all((clientes || []).map(c => tx.store.put(c)));
  await tx.done;
}
export async function getClientesCache() {
  const db = await getDb();
  return db.getAll('clientes_cache');
}

// ── Config (orgData, pdv_config, bandeiras, contas...) ──────────
export async function cacheConfig(chave, valor) {
  const db = await getDb();
  await db.put('config_cache', { chave, valor });
}
export async function getConfig(chave) {
  const db = await getDb();
  const row = await db.get('config_cache', chave);
  return row?.valor ?? null;
}

// ── Mesas/Comandas (somente leitura offline) ────────────────────
export async function cacheMesas(mesas) {
  const db = await getDb();
  const tx = db.transaction('mesas_cache', 'readwrite');
  await tx.store.clear();
  await Promise.all((mesas || []).map(m => tx.store.put(m)));
  await tx.done;
}
// orgId é obrigatório aqui: o cache não é limpo ao trocar de organização
// (mesmo dispositivo pode operar mais de uma loja/organização no mesmo
// navegador — troca de organização ou logins diferentes), então uma
// mesa/comanda em cache de OUTRA organização nunca pode ser devolvida como
// se fosse da atual — melhor mostrar vazio (sem mesa) do que mostrar a
// mesa/comanda errada aberta.
export async function getMesasCache(orgId) {
  const db = await getDb();
  const todas = await db.getAll('mesas_cache');
  return todas.filter(m => m.organizacao_id === orgId);
}
export async function cacheComanda(mesaId, comanda) {
  const db = await getDb();
  await db.put('comandas_cache', { mesa_id: mesaId, comanda });
}
export async function getComandaCache(mesaId, orgId) {
  const db = await getDb();
  const row = await db.get('comandas_cache', mesaId);
  if (!row?.comanda || row.comanda.organizacao_id !== orgId) return null;
  return row.comanda;
}

// ── Fila de sincronização de vendas ─────────────────────────────
export async function enqueueVenda(uuid, payload) {
  const db = await getDb();
  await db.put('sync_queue', {
    uuid, payload, status: 'pendente', tentativas: 0,
    criado_em: new Date().toISOString(), erro_msg: null,
  });
}
// Só as que ainda vão ser tentadas sozinhas (falha de rede). Entradas com
// status 'erro' (validação real rejeitada pelo servidor) NÃO entram aqui —
// ficam paradas esperando revisão manual (ver marcarPendenteNovamente).
export async function listPendentesSync() {
  const db = await getDb();
  const todas = await db.getAllFromIndex('sync_queue', 'status', 'pendente');
  return todas.sort((a, b) => a.criado_em.localeCompare(b.criado_em));
}
// Fila inteira (pendente + erro), pro painel mostrar pro gerente.
export async function listFilaCompleta() {
  const db = await getDb();
  const todas = await db.getAll('sync_queue');
  return todas.sort((a, b) => a.criado_em.localeCompare(b.criado_em));
}
export async function marcarSincronizando(uuid) {
  const db = await getDb();
  const row = await db.get('sync_queue', uuid);
  if (!row) return;
  row.status = 'sincronizando';
  row.tentativas += 1;
  await db.put('sync_queue', row);
}
// Falha de rede (não chegou a ter resposta do servidor) — volta pra
// 'pendente' pra ser tentada de novo sozinha no próximo ciclo.
export async function marcarPendenteNovamente(uuid) {
  const db = await getDb();
  const row = await db.get('sync_queue', uuid);
  if (!row) return;
  row.status = 'pendente';
  await db.put('sync_queue', row);
}
// Resposta real do servidor rejeitando a venda (estoque insuficiente,
// limite de crédito etc.) — para de tentar sozinha, espera revisão manual.
export async function marcarErroSync(uuid, mensagem) {
  const db = await getDb();
  const row = await db.get('sync_queue', uuid);
  if (!row) return;
  row.status = 'erro';
  row.erro_msg = mensagem;
  await db.put('sync_queue', row);
}
export async function removerDaFila(uuid) {
  const db = await getDb();
  await db.delete('sync_queue', uuid);
}
export async function contarPendentesSync() {
  const db = await getDb();
  const pendentes = await db.countFromIndex('sync_queue', 'status', 'pendente');
  const erros = await db.countFromIndex('sync_queue', 'status', 'erro');
  return pendentes + erros;
}
